import * as React from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * The search box every list page opens with.
 *
 * `type="search"` rather than `type="text"`, which keeps the browser from
 * offering to autofill it with an email address, and the native clear button is
 * suppressed in CSS in favour of ours so it looks the same in every browser and
 * can be reached by keyboard.
 *
 * Controlled, and it does not debounce. Debouncing belongs to whatever turns the
 * value into a request: a page that filters an array it already has should react
 * on the keystroke, and one that refetches should decide its own interval.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = "Search",
  label,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** The accessible name. Says what is being searched, not that it is a search. */
  label: string;
  className?: string;
}) {
  const ref = React.useRef<HTMLInputElement>(null);

  return (
    <div className={cn("search", className)}>
      <Search aria-hidden className="search__icon" />
      <input
        ref={ref}
        type="search"
        value={value}
        aria-label={label}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => onChange(event.target.value)}
        className="search__input"
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          // Focus goes back to the field: clearing is almost always the first
          // half of "search for something else".
          onClick={() => {
            onChange("");
            ref.current?.focus();
          }}
          className="search__clear"
        >
          <X />
        </button>
      )}
    </div>
  );
}
